import React, { useEffect, useState, useRef } from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import {
  Wallet as WalletIcon,
  Coins,
  Trophy,
  Lock,
  ArrowDownToLine,
  History,
  CreditCard,
  PencilLine,
  Info,
  ShieldCheck,
  ChevronRight,
  Plus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import RechargeModal from "../../../components/Wallet/RechargeModal";
import TransactionHistory from "../../../components/Wallet/TransactionHistory";
import RechargeHistory from "../../../components/Wallet/RechargeHistory";
import {
  getWalletBalance,
  unifiedWithdraw,
} from "../../../services/walletService";
import { useUser } from "../../../hooks/use-user";
import axios from "../../../axios";
import { Link } from "react-router-dom";

const Wallet = () => {
  const { data: user, refetch } = useUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [walletData, setWalletData] = useState({
    totalBalance: 0,
    availableBalance: 0,
    lockedBalance: 0,
    rechargedBalance: 0,
    contestBalance: 0,
    lifetimeEarned: 0,
    lifetimeContestEarned: 0,
    lifetimeRecharged: 0,
    lifetimeReferralEarned: 0,
    referralBalance: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Unified Withdraw State
  const [withAmount, setWithAmount] = useState("");
  const [isFormVisible, setFormVisible] = useState(false);
  const intial = {
    aadharNo: "",
    ifsc: "",
    bankName: "",
    accountNo: "",
    holderName: "",
    mobile: "",
    email: "",
    upiId: "",
  };
  const [formData, setFormData] = useState(intial);
  const [hasExistingKyc, setHasExistingKyc] = useState(false);
  const firstInputRef = useRef(null);

  // Fetch existing KYC data
  const fetchKycData = async () => {
    try {
      const response = await axios.get("getKyc");
      if (response.data?.result) {
        const kycData = response.data.result;
        setFormData({
          aadharNo: kycData.aadharNo || "",
          ifsc: kycData.ifsc || "",
          bankName: kycData.bankName || "",
          accountNo: kycData.accountNo || "",
          holderName: kycData.holderName || "",
          mobile: kycData.mobile || "",
          email: kycData.email || "",
          upiId: kycData.upiId || "",
        });
        setHasExistingKyc(true);
      }
    } catch (error) {
      console.log("No existing KYC found");
      setHasExistingKyc(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getWalletBalance();
      setWalletData(data.result);
      if (user) {
        refetch();
        setFormData((prev) => ({
          ...prev,
          email: user.email || prev.email,
          mobile: user.mobile || prev.mobile,
        }));
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchKycData();
  }, []);

  // --- Unified Withdraw Logic ---
  const sendWithdrawRequest = async (e) => {
    e.preventDefault();

    if (!user?.kycDone) {
      toast({
        title: "KYC Required",
        description: "Please complete your KYC details to withdraw funds.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      setFormVisible(true);
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
      return;
    }

    if (withAmount <= 0) {
      toast({
        title: "Please enter an amount greater than 0.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      await unifiedWithdraw(Number(withAmount));
      toast({
        title: "Withdraw request sent successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setWithAmount("");
      fetchData();
    } catch (error) {
      console.log("Withdrawal Error:", error);
      let message = "Withdrawal failed";

      if (error.response) {
        message =
          error.response.data?.message ||
          error.response.data?.error ||
          JSON.stringify(error.response.data);
      } else if (error.request) {
        message =
          "No response from server. Please check your internet connection.";
      } else {
        message = error.message;
      }

      toast({
        title: "Withdrawal Failed",
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendKyc = async (e) => {
    e.preventDefault();
    try {
      if (hasExistingKyc) {
        await axios.put("updateKyc", formData);
      } else {
        await axios.post("addKyc", formData);
      }
      toast({
        title: hasExistingKyc
          ? "KYC updated successfully."
          : "KYC details sent successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setHasExistingKyc(true);
      refetch();
    } catch (err) {
      toast({
        title: "Something went wrong.",
        description: err?.response?.data?.message || err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const tabs = [
    { label: "Withdraw", icon: ArrowDownToLine },
    { label: "Transactions", icon: History },
    { label: "Recharge History", icon: CreditCard },
  ];

  const statCards = [
    {
      label: "Total Balance",
      value: walletData.totalBalance,
      icon: WalletIcon,
      color: "from-purple-500 to-indigo-500",
      borderColor: "border-purple-500/30",
      helpText: "All Coins",
    },
    {
      label: "Recharged (Play Only)",
      value: walletData.rechargedBalance,
      icon: Coins,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-500/30",
      helpText: "Purchased Coins",
    },
    {
      label: "Contest & Referral",
      value:
        (walletData.contestBalance || 0) + (walletData.referralBalance || 0),
      icon: Trophy,
      color: "from-orange-500 to-amber-500",
      borderColor: "border-orange-500/30",
      helpText: "Withdrawable Anytime",
    },
    {
      label: "Quiz Earnings",
      value: walletData.lockedBalance,
      icon: Lock,
      color: "from-gray-400 to-gray-500",
      borderColor: "border-gray-500/30",
      helpText: walletData.isLockReleased ? "Unlocked" : "Releases soon",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2A1B3D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">Loading wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2A1B3D] pt-20 pb-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-purple-900/20 to-transparent pointer-events-none"></div>
      <div className="absolute top-[-100px] right-[-100px] w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-[-100px] w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10"
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <WalletIcon size={28} className="text-purple-400" />
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                Unified Wallet
              </h1>
            </div>
            <p className="text-gray-400 text-sm md:text-base">
              Manage all your earnings in one place
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onOpen}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
          >
            <Plus size={18} />
            Add Coins
          </motion.button>
        </motion.div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {statCards.map((card, index) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`group relative bg-[#3F234E] rounded-2xl p-5 border border-white/5 hover:border-purple-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/10`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} bg-opacity-20 flex items-center justify-center`}
                  style={{ background: `linear-gradient(135deg, rgba(139,92,246,0.2), rgba(99,102,241,0.2))` }}
                >
                  <card.icon size={20} className="text-white/80" />
                </div>
                <span className="text-gray-400 text-xs font-medium uppercase tracking-wider">
                  {card.label}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl md:text-3xl font-bold text-white">
                  {card.value}
                </span>
                <span className="text-xs text-gray-500">coins</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{card.helpText}</p>
              {/* Gradient accent line */}
              <div
                className={`absolute bottom-0 left-4 right-4 h-0.5 bg-gradient-to-r ${card.color} rounded-full opacity-50`}
              ></div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-[#3F234E] rounded-3xl border border-white/5 overflow-hidden"
        >
          {/* Tab Header */}
          <div className="flex border-b border-white/5 overflow-x-auto">
            {tabs.map((tab, index) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(index)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-bold whitespace-nowrap transition-all relative
                  ${
                    activeTab === index
                      ? "text-white"
                      : "text-gray-400 hover:text-gray-300"
                  }`}
              >
                <tab.icon size={16} />
                {tab.label}
                {activeTab === index && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              {activeTab === 0 && (
                <motion.div
                  key="withdraw"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex lg:flex-row flex-col gap-8">
                    {/* Left: Withdraw Form */}
                    <div className="lg:w-1/2">
                      {/* Priority Info Box */}
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <Info
                            size={18}
                            className="text-purple-400 mt-0.5 flex-shrink-0"
                          />
                          <div>
                            <p className="text-purple-300 text-sm font-bold mb-2">
                              Unified Withdrawal Priority:
                            </p>
                            <ol className="space-y-1">
                              <li className="text-purple-200/80 text-xs">
                                1. Contest Winnings (First)
                              </li>
                              <li className="text-purple-200/80 text-xs">
                                2. Referral Winnings
                              </li>
                              <li className="text-purple-200/80 text-xs">
                                3. Quiz Earnings (Unlocked)
                              </li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      <form
                        onSubmit={sendWithdrawRequest}
                        className="max-w-md"
                      >
                        <p className="text-white font-bold text-sm mb-3">
                          Enter Amount to Withdraw
                        </p>
                        <div className="relative">
                          <input
                            type="number"
                            className="block w-full p-4 text-sm text-white bg-[#2A1B3D] border border-white/10 rounded-xl focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500 outline-none transition-colors"
                            placeholder="Enter amount..."
                            required
                            value={withAmount}
                            onChange={(e) => setWithAmount(e.target.value)}
                          />
                          <button
                            type="submit"
                            className="absolute right-2 bottom-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-purple-500/40 text-white font-medium rounded-lg text-sm px-5 py-2.5 shadow-lg shadow-purple-500/25 transition-shadow"
                            disabled={loading}
                          >
                            Withdraw
                          </button>
                        </div>
                      </form>

                      <div className="flex items-center justify-between max-w-md mt-4">
                        <span className="text-gray-500 text-xs">
                          Min. Withdraw: 100 Coins
                        </span>
                        <Link to="/dash/withdrawal-history">
                          <span className="text-purple-400 text-xs font-medium hover:text-purple-300 transition-colors flex items-center gap-1">
                            View History
                            <ChevronRight size={14} />
                          </span>
                        </Link>
                      </div>
                    </div>

                    {/* Right: KYC / Bank Details */}
                    <div className="lg:w-1/2">
                      <div className="bg-[#2A1B3D]/50 border border-white/5 rounded-2xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <ShieldCheck
                              size={20}
                              className="text-purple-400"
                            />
                            <h3 className="text-white font-bold text-lg">
                              KYC / Bank Details
                            </h3>
                          </div>
                          <button
                            onClick={() => setFormVisible(!isFormVisible)}
                            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                          >
                            <PencilLine
                              size={16}
                              className="text-gray-400 hover:text-purple-400 transition-colors"
                            />
                          </button>
                        </div>

                        <p className="text-gray-500 text-sm mb-4">
                          {hasExistingKyc
                            ? "Your bank details are saved."
                            : "Please add your bank details to process withdrawals."}
                        </p>

                        {(isFormVisible || !hasExistingKyc) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="space-y-3"
                          >
                            {[
                              {
                                name: "holderName",
                                placeholder: "Account Holder Name",
                                type: "text",
                              },
                              {
                                name: "aadharNo",
                                placeholder: "Aadhaar Number",
                                type: "text",
                              },
                              {
                                name: "accountNo",
                                placeholder: "Account Number",
                                type: "text",
                              },
                              {
                                name: "ifsc",
                                placeholder: "IFSC Code",
                                type: "text",
                              },
                              {
                                name: "bankName",
                                placeholder: "Bank Name",
                                type: "text",
                              },
                              {
                                name: "upiId",
                                placeholder: "UPI ID",
                                type: "text",
                              },
                              {
                                name: "mobile",
                                placeholder: "Mobile Number",
                                type: "text",
                              },
                            ].map((field, idx) => (
                              <input
                                key={field.name}
                                ref={idx === 0 ? firstInputRef : undefined}
                                type={field.type}
                                name={field.name}
                                placeholder={field.placeholder}
                                value={formData[field.name]}
                                onChange={handleChange}
                                className="w-full p-3 text-sm text-white bg-[#2A1B3D] border border-white/10 rounded-xl focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500 outline-none transition-colors"
                              />
                            ))}
                            <button
                              onClick={sendKyc}
                              className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-sm rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all hover:scale-[1.01] active:scale-[0.99]"
                            >
                              {hasExistingKyc
                                ? "Update Details"
                                : "Save Details"}
                            </button>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 1 && (
                <motion.div
                  key="transactions"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <TransactionHistory />
                </motion.div>
              )}

              {activeTab === 2 && (
                <motion.div
                  key="recharge"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <RechargeHistory />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      <RechargeModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={fetchData}
        user={user}
        walletBalance={walletData.totalBalance}
      />
    </div>
  );
};

export default Wallet;
